import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Sparkles, FileText, Eye } from "lucide-react";
import { useReference } from "@/lib/reference";

const STATUS_COLOR = {
  Draft: "bg-[#6B5944]/15 text-[#2A1810]",
  Sent: "bg-[#5D3A1F]/15 text-[#5D3A1F]",
  Accepted: "bg-[#6B8E5A] text-white",
  Rejected: "bg-[#A23E3E]/15 text-[#A23E3E]",
  Expired: "bg-[#D4A547]/15 text-[#A6741F]",
};

const emptyProposal = {
  client_name: "", client_id: "", lead_id: "", workshop_type: "Customer Service Excellence",
  amount: 0, status: "Draft", expiry_date: "", content: "", notes: "",
};

const ProposalForm = ({ open, onOpenChange, initial, onSubmit, mode, clients, leads }) => {
  const ref = useReference();
  const [form, setForm] = useState(initial || emptyProposal);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [model, setModel] = useState("claude-sonnet-4-6");

  const handleOpenChange = (v) => { if (v) setForm(initial || emptyProposal); onOpenChange(v); };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try { await onSubmit({ ...form, amount: Number(form.amount || 0) }); handleOpenChange(false); } finally { setSaving(false); }
  };

  // Linked lead — surfaces needs assessment fill state to the UI
  const linkedLead = (leads || []).find((l) => l.id === form.lead_id);
  const naFilled = linkedLead?.needs_assessment
    ? Object.values(linkedLead.needs_assessment).filter((v) => (v || "").trim()).length
    : 0;

  const draftWithAI = async () => {
    if (!form.client_name) { toast.error("Set client name first"); return; }
    setAiLoading(true);
    try {
      const res = await api.post("/ai/proposal", {
        client_name: form.client_name, workshop_type: form.workshop_type,
        amount: Number(form.amount || 0), duration: "1 day", participants: 15,
        context: form.notes, lead_id: form.lead_id || null, model,
      });
      setF("content", res.data.content);
      if (res.data.used_needs_assessment) {
        toast.success("✨ Tailored proposal drafted using the needs assessment");
      } else {
        toast.success("AI proposal drafted");
      }
    } catch (e) {
      toast.error("AI draft failed: " + (e?.response?.data?.detail || e.message));
    } finally { setAiLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="proposal-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{mode === "create" ? "New proposal" : "Edit proposal"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="space-y-2">
            <Label>Client *</Label>
            <Select value={form.client_id || "manual"} onValueChange={(v) => {
              if (v === "manual") { setF("client_id", ""); return; }
              const c = clients.find((x) => x.id === v);
              setF("client_id", v); setF("client_name", c?.organization_name || "");
            }}>
              <SelectTrigger data-testid="prop-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">— Enter manually —</SelectItem>
                {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Client Name *</Label>
            <Input data-testid="prop-client-input" value={form.client_name} onChange={(e) => setF("client_name", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Workshop Type</Label>
            <Select value={form.workshop_type} onValueChange={(v) => setF("workshop_type", v)}>
              <SelectTrigger data-testid="prop-workshop-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ref.services.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Amount (USD)</Label><Input type="number" min="0" value={form.amount} onChange={(e) => setF("amount", e.target.value)} data-testid="prop-amount-input" /></div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setF("status", v)}>
              <SelectTrigger data-testid="prop-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ref.proposal_statuses.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Expiry Date</Label><Input type="date" value={form.expiry_date} onChange={(e) => setF("expiry_date", e.target.value)} data-testid="prop-expiry-input" /></div>

          {/* Link to Lead for tailored AI */}
          <div className="space-y-2 md:col-span-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="flex items-center gap-2">
                Link to Lead (for tailored AI draft)
                {naFilled > 0 && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#C28A2E] font-bold">
                    <Sparkles className="w-3 h-3" /> {naFilled} needs-assessment answers ready
                  </span>
                )}
              </Label>
            </div>
            <Select value={form.lead_id || "none"} onValueChange={(v) => {
              if (v === "none") { setF("lead_id", ""); return; }
              const l = (leads || []).find((x) => x.id === v);
              setF("lead_id", v);
              if (l && !form.client_name) setF("client_name", l.organization_name);
            }}>
              <SelectTrigger data-testid="prop-lead-select"><SelectValue placeholder="Optional — pick a lead to auto-tailor the AI draft" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None (generic AI draft) —</SelectItem>
                {(leads || []).map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.organization_name} — {l.contact_name}
                    {l.needs_assessment && Object.values(l.needs_assessment).some((v) => (v || "").trim()) ? " ✦" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {linkedLead && (
              <div className="text-xs text-[#6B5944] bg-[#FDFBF5] border border-[#EBE3D2] rounded-lg p-3" data-testid="linked-lead-preview">
                <strong className="text-[#5D3A1F]">Linked:</strong> {linkedLead.organization_name} · {linkedLead.contact_name}
                {naFilled === 0 ? (
                  <div className="mt-1 text-[#A6741F]">⚠ No needs-assessment answers captured yet. Open this lead and fill the <em>Needs Assessment</em> tab so the AI can tailor the proposal.</div>
                ) : (
                  <div className="mt-1 text-[#6B8E5A]">✓ The AI will use the {naFilled} captured answer(s) (industry, challenge, desired outcome, etc.) to write a tailored proposal.</div>
                )}
              </div>
            )}
          </div>
          <div className="space-y-2 md:col-span-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label>Proposal Content</Label>
              <div className="flex items-center gap-2">
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger className="h-9 w-[180px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="claude-sonnet-4-6">Claude Sonnet 4.5</SelectItem>
                    <SelectItem value="gpt-5.2">GPT-5.2</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="sm" onClick={draftWithAI} disabled={aiLoading}
                  className="text-[#C28A2E] border-[#C28A2E]/30 hover:bg-[#C28A2E]/5" data-testid="prop-ai-draft-btn">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {aiLoading ? "Drafting…" : "AI draft"}
                </Button>
              </div>
            </div>
            <Textarea rows={12} value={form.content} onChange={(e) => setF("content", e.target.value)}
              placeholder="Proposal narrative — or click 'AI draft' to generate one." data-testid="prop-content-input" />
          </div>
          <div className="space-y-2 md:col-span-2"><Label>Internal Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setF("notes", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="prop-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.client_name} className="bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="prop-form-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PreviewDialog = ({ proposal, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="proposal-preview-dialog">
      {proposal && (
        <>
          <DialogHeader>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">{proposal.proposal_number}</div>
            <DialogTitle className="font-display text-2xl">{proposal.client_name}</DialogTitle>
            <div className="text-sm text-[#6B5944]">{proposal.workshop_type} · ${Number(proposal.amount).toLocaleString()}</div>
          </DialogHeader>
          <div className="ccs-prose mt-4 max-w-none">
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{proposal.content || "No content yet."}</pre>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

const ProposalsPage = () => {
  const [proposals, setProposals] = useState([]);
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([api.get("/proposals"), api.get("/clients"), api.get("/leads")])
      .then(([p, c, l]) => { setProposals(p.data); setClients(c.data); setLeads(l.data); })
      .finally(() => setLoading(false));
  };
  useEffect(fetchAll, []);

  const onCreate = async (d) => { try { await api.post("/proposals", d); toast.success("Proposal created"); fetchAll(); } catch (e) { toast.error("Failed"); throw e; } };
  const onUpdate = async (d) => { try { await api.put(`/proposals/${editing.id}`, d); toast.success("Updated"); fetchAll(); } catch (e) { toast.error("Failed"); throw e; } };
  const onDelete = async (id) => { if (!window.confirm("Delete this proposal?")) return; await api.delete(`/proposals/${id}`); toast.success("Deleted"); fetchAll(); };

  return (
    <div className="space-y-6" data-testid="proposals-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Proposals</div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight mt-1.5">Proposal Management</h1>
          <p className="text-[#6B5944] mt-1.5">Draft, send, and close — with AI-powered proposal writing.</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-proposal-btn"
          className="bg-[#C28A2E] hover:bg-[#A6741F] text-white rounded-xl h-11 px-5 hover:-translate-y-[1px] transition-all">
          <Plus className="w-4 h-4 mr-2" /> New proposal
        </Button>
      </div>

      {loading ? <div className="text-center py-16 text-[#6B5944]">Loading…</div> : proposals.length === 0 ? (
        <div className="ccs-card p-12 text-center">
          <FileText className="w-10 h-10 text-[#C28A2E] mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-display text-xl font-medium text-[#2A1810]">No proposals yet</div>
          <p className="text-[#6B5944] text-sm mt-1">Use the AI assistant to draft your first proposal in seconds.</p>
        </div>
      ) : (
        <div className="ccs-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-[#EBE3D2] hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] py-4">Number</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">Client</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">Workshop</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] text-right">Amount</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">Expires</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {proposals.map((p) => (
                <TableRow key={p.id} className="border-[#EBE3D2]" data-testid={`proposal-row-${p.id}`}>
                  <TableCell className="py-4 font-mono text-xs text-[#6B5944]">{p.proposal_number}</TableCell>
                  <TableCell className="font-medium">{p.client_name}</TableCell>
                  <TableCell className="text-sm text-[#6B5944]">{p.workshop_type}</TableCell>
                  <TableCell className="text-right font-semibold text-[#5D3A1F]">${Number(p.amount || 0).toLocaleString()}</TableCell>
                  <TableCell><Badge className={`${STATUS_COLOR[p.status]} rounded-full font-medium px-3`}>{p.status}</Badge></TableCell>
                  <TableCell className="text-sm text-[#6B5944]">{p.expiry_date || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => { setPreview(p); setPreviewOpen(true); }} data-testid={`view-proposal-${p.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => { setEditing(p); setFormOpen(true); }} data-testid={`edit-proposal-${p.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => onDelete(p.id)}>
                      <Trash2 className="w-4 h-4 text-[#A23E3E]" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ProposalForm open={formOpen} onOpenChange={setFormOpen} initial={editing}
        mode={editing ? "edit" : "create"} onSubmit={editing ? onUpdate : onCreate} clients={clients} leads={leads} />
      <PreviewDialog proposal={preview} open={previewOpen} onOpenChange={setPreviewOpen} />
    </div>
  );
};

export default ProposalsPage;
